"use client";

import { ReactNode, forwardRef, useLayoutEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import parse from "html-react-parser";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, useScroll, useTransform } from "framer-motion";
import type { IPageContent } from "@/types/pageContent.types";
import FooterViewV15 from "@/components/Footer/FooterViewV15";
import Scene3CardsSliderV15 from "./Scene3CardsSliderV15";
import Scene11CardsSliderV15 from "./Scene11CardsSliderV15";

import styles from "./styles.module.scss";
import TransitionLink from "@/components/TransitionLink";

gsap.registerPlugin(ScrollTrigger);
// blur
const SECTION_FADE_IN_DELAY_MS = 500;
const SECTION_FADE_IN_DURATION_MS = 2000;
const SECTION_FADE_OUT_DURATION_MS = 800;
const MotionImage = motion.create(Image);

type TrackingDeviceOverlayProps = {
  children?: ReactNode;
  stepCount?: number;
  isModelReady?: boolean;
  ionTrackingDevice3dSection1Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSection1"];
  ionTrackingDevice3dSection2Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSection2"];
  ionTrackingDevice3dSectionHoriCardsData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSectionHoriCards"];
  ionTrackingDevice3dSectionPennyScaleData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSectionPennyScale"];
  ionTrackingDevice3dSection5Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSection5"];
  ionTrackingDevice3dSectionCoreCapabilitiesData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSectionCoreCapabilities"];
  ionTrackingDeviceImplementationProcessData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDeviceImplementationProcess"];

  ionTrackingDevicePag2Section1Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section1"];
  ionTrackingDevicePag2Section2Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section2"];
  ionTrackingDevicePag2Section3Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section3"];
  ionTrackingDevicePag2Section4Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section4"];
  ionTrackingDevicePag2Section5Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section5"];
  ionTrackingDevicePag2Section6Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section6"];

  ionTrackingDeviceAppliedAcrossComplexData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDeviceAppliedAcrossComplex"];
  footerData: IPageContent["footerModelV15"]["footer"];
};

const TrackingDeviceOverlay = forwardRef<HTMLDivElement, TrackingDeviceOverlayProps>((
  {
    children,
    stepCount = 10,
    isModelReady = false,
    ionTrackingDevice3dSection1Data,
    ionTrackingDevice3dSection2Data,
    ionTrackingDevice3dSectionHoriCardsData,
    ionTrackingDevice3dSectionPennyScaleData,
    ionTrackingDevice3dSection5Data,
    ionTrackingDevice3dSectionCoreCapabilitiesData,
    ionTrackingDeviceImplementationProcessData,
    ionTrackingDevicePag2Section1Data,
    ionTrackingDevicePag2Section2Data,
    ionTrackingDevicePag2Section3Data,
    ionTrackingDevicePag2Section4Data,
    ionTrackingDevicePag2Section5Data,
    ionTrackingDevicePag2Section6Data,
    ionTrackingDeviceAppliedAcrossComplexData,
    footerData
  }, 
  forwardedRef
) => {
  const overlayScrollRef = useRef<HTMLDivElement | null>(null);
  const overlaySpaceRef = useRef<HTMLDivElement | null>(null);
  const frameOneRef = useRef<HTMLDivElement | null>(null);
  const frameOneFadeRef = useRef<HTMLDivElement | null>(null);
  const frameTwoRef = useRef<HTMLDivElement | null>(null);
  const frameTwoFadeRef = useRef<HTMLDivElement | null>(null);
  const frameThreeRef = useRef<HTMLDivElement | null>(null);
  const frameThreeViewportRef = useRef<HTMLDivElement | null>(null);
  const frameThreeTrackRef = useRef<HTMLDivElement | null>(null);
  const frameFourRef = useRef<HTMLDivElement | null>(null);
  const frameFiveRef = useRef<HTMLDivElement | null>(null);
  const frameSixRef = useRef<HTMLDivElement | null>(null);
  const frameSevenRef = useRef<HTMLDivElement | null>(null);
  const frameSevenImageRef = useRef<HTMLDivElement | null>(null);
  const frameEightRef = useRef<HTMLElement | null>(null);
  const frameNineRef = useRef<HTMLElement | null>(null);
  const frameTenRef = useRef<HTMLElement | null>(null);
  const frameElevenRef = useRef<HTMLElement | null>(null);
  const frameTwelveRef = useRef<HTMLElement | null>(null);
  const frameThirteenRef = useRef<HTMLElement | null>(null);
  const frameFourteenRef = useRef<HTMLElement | null>(null);
  const frameTenFadeRef = useRef<HTMLDivElement | null>(null);
  const frameElevenFadeRef = useRef<HTMLDivElement | null>(null);
  const frameTwelveFadeRef = useRef<HTMLDivElement | null>(null);
  const frameThirteenFadeRef = useRef<HTMLDivElement | null>(null);
  const frameFourteenFadeRef = useRef<HTMLDivElement | null>(null);
  const frameFifteenRef = useRef<HTMLElement | null>(null);
  const scene9Ref = useRef<HTMLDivElement>(null);
  const [sceneTenActiveIndex, setSceneTenActiveIndex] = useState(0);
  const [sceneTenPaginationVisible, setSceneTenPaginationVisible] = useState(false);
  const [isSceneTenJumpScrolling, setIsSceneTenJumpScrolling] = useState(false);
  const [isSceneFifteenUnlocked, setIsSceneFifteenUnlocked] = useState(false);
  const horizontalCards = ionTrackingDevice3dSectionHoriCardsData.slice(1, 7);
  const stickyContent = useMemo(
    () => [
      { data: ionTrackingDevicePag2Section2Data, id: "pag2section2" },
      { data: ionTrackingDevicePag2Section3Data, id: "pag2section3" },
      { data: ionTrackingDevicePag2Section4Data, id: "pag2section4" },
      { data: ionTrackingDevicePag2Section5Data, id: "pag2section5" },
      { data: ionTrackingDevicePag2Section6Data, id: "pag2section6" },
    ],
    [
      ionTrackingDevicePag2Section2Data,
      ionTrackingDevicePag2Section3Data,
      ionTrackingDevicePag2Section4Data,
      ionTrackingDevicePag2Section5Data,
      ionTrackingDevicePag2Section6Data,
    ]
  );
  const scene10_1 = stickyContent[0];
  const scene10_2 = stickyContent[1];
  const scene10_3 = stickyContent[2];
  const scene10_4 = stickyContent[3];
  const scene10_5 = stickyContent[4];
  const appliedAcrossComplexHero =
    ionTrackingDeviceAppliedAcrossComplexData[0]?.__typename ===
    "OverlineHeadingBodyGalleryRecord"
      ? ionTrackingDeviceAppliedAcrossComplexData[0]
      : null;
  const { scrollYProgress: scene9ScrollProgress } = useScroll({
    target: scene9Ref,
    offset: ["start end", "end start"],
  });
  const scene9BackgroundY = useTransform(
    scene9ScrollProgress,
    [0, 1],
    [-100, 100]
  );

  const renderSceneTenSection = (
    sectionRef: { current: HTMLElement | null },
    fadeRef: { current: HTMLDivElement | null },
    sectionClassName: string,
    item: (typeof stickyContent)[number]
  ) => (
    <section
      ref={sectionRef}
      className={`${styles.scrollTextCard} ${sectionClassName}`}
    >
      <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.sceneTenItem}>
            <div ref={fadeRef} className={styles.sceneTenFadeContainer}>
              <div className={styles.copyContainer}>
                <h3>{parse(item.data.heading ?? "")}</h3>
                <p>{parse(item.data.body ?? "")}</p>
              </div>
              <div className={styles.imgContainer}>
                {item.data.gallery?.[0] ? (
                  <Image
                    src={item.data.gallery[0].url}
                    alt={item.data.gallery[0].alt}
                    fill
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
    </section>
  );

  const sceneTenSectionRefs = [
    frameTenRef,
    frameElevenRef,
    frameTwelveRef,
    frameThirteenRef,
    frameFourteenRef,
  ];
  const sceneTenFadeRefs = [
    frameTenFadeRef,
    frameElevenFadeRef,
    frameTwelveFadeRef,
    frameThirteenFadeRef,
    frameFourteenFadeRef,
  ];
  const scrollToSceneTenSection = (index: number) => {
    const scroller = overlayScrollRef.current;
    const section = sceneTenSectionRefs[index]?.current;

    if (!scroller || !section) {
      return;
    }

    const targetTop =
      section.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;

    gsap.to(scroller, {
      scrollTop: targetTop,
      duration: 0.7,
      ease: "power2.out",
      overwrite: true,
      onStart: () => {
        setIsSceneTenJumpScrolling(true);
      },
      onComplete: () => {
        setIsSceneTenJumpScrolling(false);
      },
      onInterrupt: () => {
        setIsSceneTenJumpScrolling(false);
      },
    });
  };

  useLayoutEffect(() => {
    const scroller = overlayScrollRef.current;
    const sectionElements = [
      frameOneRef.current,
      frameTwoRef.current,
      frameThreeRef.current,
      frameFourRef.current,
      frameFiveRef.current,
      frameSixRef.current,
      frameSevenRef.current,
      frameEightRef.current,
      frameNineRef.current,
      frameTenRef.current,
      frameElevenRef.current,
      frameTwelveRef.current,
      frameThirteenRef.current,
      frameFourteenRef.current,
      frameFifteenRef.current,
    ];

    const fadeElements = [
      frameOneFadeRef.current,
      frameTwoFadeRef.current,
      frameThreeRef.current?.firstElementChild as HTMLElement | null,
      frameFourRef.current?.firstElementChild as HTMLElement | null,
      frameFiveRef.current?.firstElementChild as HTMLElement | null,
      frameSixRef.current?.firstElementChild as HTMLElement | null,
      frameSevenRef.current?.firstElementChild as HTMLElement | null,
      frameEightRef.current?.firstElementChild as HTMLElement | null,
      frameNineRef.current?.firstElementChild as HTMLElement | null,
      ...sceneTenFadeRefs.map((fadeRef) => fadeRef.current),
      frameFifteenRef.current?.firstElementChild as HTMLElement | null,
    ];

    if (
      !scroller ||
      sectionElements.some((section) => !section) ||
      fadeElements.some((fade) => !fade)
    ) {
      return;
    }

    const cleanup: Array<() => void> = [];
    const sectionFadeOutDuration = SECTION_FADE_OUT_DURATION_MS / 1000;
    const laterSectionFadeDelayMs = SECTION_FADE_IN_DELAY_MS;
    const laterSectionFadeDurationMs = SECTION_FADE_IN_DURATION_MS;

    const addSectionFade = ({
      section,
      fade,
      nextSection,
      delayMs,
      durationMs,
      revealOnMount = false,
    }: {
      section: HTMLElement;
      fade: HTMLElement;
      nextSection?: HTMLElement | null;
      delayMs: number;
      durationMs: number;
      revealOnMount?: boolean;
    }) => {
      gsap.set(fade, { autoAlpha: 0 });

      const revealTween = gsap.to(fade, {
        autoAlpha: 1,
        duration: durationMs / 1000,
        delay: delayMs / 1000,
        ease: "power1.out",
        paused: true,
      });

      if (revealOnMount) {
        revealTween.restart(true);
      }

      const entryTrigger = ScrollTrigger.create({
        trigger: section,
        scroller,
        start: "top 75%",
        end: "top 50%",
        invalidateOnRefresh: true,
        onEnter: () => {
          revealTween.restart(true);
        },
        onEnterBack: () => {
          revealTween.restart(true);
        },
        onLeaveBack: () => {
          revealTween.pause(0);
          gsap.set(fade, { autoAlpha: 0 });
        },
      });

      cleanup.push(() => {
        entryTrigger.kill();
        revealTween.kill();
      });

      if (nextSection) {
        const exitTrigger = ScrollTrigger.create({
          trigger: nextSection,
          scroller,
          start: "top 75%",
          end: "top 50%",
          invalidateOnRefresh: true,
          onEnter: () => {
            gsap.to(fade, {
              autoAlpha: 0,
              duration: sectionFadeOutDuration,
              ease: "power1.out",
            });
          },
          onEnterBack: () => {
            revealTween.restart(true);
          },
        });

        cleanup.push(() => {
          exitTrigger.kill();
        });
      }
    };

    addSectionFade({
      section: sectionElements[0] as HTMLElement,
      fade: fadeElements[0] as HTMLElement,
      nextSection: sectionElements[1] as HTMLElement,
      delayMs: SECTION_FADE_IN_DELAY_MS,
      durationMs: SECTION_FADE_IN_DURATION_MS,
      revealOnMount: true,
    });

    for (let index = 1; index < sectionElements.length - 1; index += 1) {
      addSectionFade({
        section: sectionElements[index] as HTMLElement,
        fade: fadeElements[index] as HTMLElement,
        nextSection: sectionElements[index + 1] as HTMLElement | undefined,
        delayMs: laterSectionFadeDelayMs,
        durationMs: laterSectionFadeDurationMs,
      });
    }

    const frameFifteenContent = frameFifteenRef.current?.firstElementChild as HTMLElement | null;
    if (frameFifteenContent) {
      gsap.set(frameFifteenContent, { autoAlpha: 1 });
    }

    return () => {
      cleanup.forEach((dispose) => dispose());
    };
  }, []);

  useLayoutEffect(() => {
    const scroller = overlayScrollRef.current;
    const sceneTenSections = sceneTenSectionRefs.map((sectionRef) => sectionRef.current);

    if (!scroller || sceneTenSections.some((section) => !section)) {
      return;
    }

    const triggers = sceneTenSections.map((section, index) =>
      ScrollTrigger.create({
        trigger: section as HTMLElement,
        scroller,
        start: "top 60%",
        end: "bottom 40%",
        invalidateOnRefresh: true,
        onEnter: () => {
          setSceneTenPaginationVisible(true);
          setSceneTenActiveIndex(index);
        },
        onEnterBack: () => {
          setSceneTenPaginationVisible(true);
          setSceneTenActiveIndex(index);
        },
        onToggle: (self) => {
          if (self.isActive) {
            setSceneTenPaginationVisible(true);
            setSceneTenActiveIndex(index);
          }
        },
        onLeave: index === sceneTenSections.length - 1
          ? () => {
              setSceneTenPaginationVisible(false);
            }
          : undefined,
        onLeaveBack: index === 0
          ? () => {
              setSceneTenPaginationVisible(false);
            }
          : undefined,
      })
    );

    const activeTriggerIndex = triggers.findIndex((trigger) => trigger.isActive);

    if (activeTriggerIndex >= 0) {
      setSceneTenActiveIndex(activeTriggerIndex);
      setSceneTenPaginationVisible(true);
    } else {
      setSceneTenActiveIndex(0);
      setSceneTenPaginationVisible(false);
    }

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  useLayoutEffect(() => {
    const scroller = overlayScrollRef.current;
    const section = frameFifteenRef.current;

    if (!scroller || !section) {
      return;
    }

    const unlockTrigger = ScrollTrigger.create({
      trigger: section,
      scroller,
      start: "top 75%",
      end: "bottom 25%",
      invalidateOnRefresh: true,
      onEnter: () => {
        setIsSceneFifteenUnlocked(true);
      },
      onEnterBack: () => {
        setIsSceneFifteenUnlocked(true);
      },
      onLeaveBack: () => {
        setIsSceneFifteenUnlocked(false);
      },
    });

    return () => {
      unlockTrigger.kill();
    };
  }, []);

  const setOverlayRef = (node: HTMLDivElement | null) => {
    overlayScrollRef.current = node;

    if (typeof forwardedRef === "function") {
      forwardedRef(node);
      return;
    }

  if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  // console.log('ionTrackingDevice3dSection1Data ', ionTrackingDevice3dSection1Data)

  return (
    <div
      ref={setOverlayRef}
      className={`${styles.overlayScrollViewport} ${
        isSceneTenJumpScrolling || isSceneFifteenUnlocked
          ? styles.overlayScrollViewportNoSnap
          : ""
      }`}
      aria-label="Tracking Device Story"
    >
      {/* ---------- Scene1 : Adaptable Tracking Hardware ... ---------- */}
          <section
            ref={frameOneRef}
            className={`${styles.scrollTextCard} ${styles.overlayFrameOne}`}
          >
            <div
              className={`${styles.modelHideOverlay}`}
              style={{
                opacity: isModelReady ? 0 : 1,
                transition: isModelReady
                  ? "opacity 900ms ease-out 2000ms"
                  : "none",
              }}
            />
            <div
              ref={frameOneFadeRef}
              className={`${styles.fadeContainer}`}
              style={{ opacity: 0 }}
            >
              <span>
                {ionTrackingDevice3dSection1Data.overline}
              </span>
              <h2>
                {parse(ionTrackingDevice3dSection1Data.heading)}
              </h2>
              <p>
                {ionTrackingDevice3dSection1Data.body}
              </p>
              <TransitionLink href="/contact-us" className={styles.sceneOneCta}>
                {ionTrackingDevice3dSection1Data.button}
              </TransitionLink>
            </div>
          </section>


          {/* ---------- Scene2 : One Proprietary Core ... ---------- */}
          <section ref={frameTwoRef} className={`${styles.scrollTextCard} ${styles.overlayFrameTwo}`}>
            <div
              ref={frameTwoFadeRef}
              className={`${styles.fadeContainer}`}
            >
              <span>
                {ionTrackingDevice3dSection2Data.overline}
              </span>
              <h2>
                {parse(ionTrackingDevice3dSection2Data.heading)}
              </h2>
              <p>
                {ionTrackingDevice3dSection2Data.body}
              </p>
            </div>
          </section>


          {/* ---------- Scene3 : A Smarter Standard for Tracking ---------- */}
          <section ref={frameThreeRef} className={`${styles.scrollTextCard} ${styles.overlayFrameThree}`}>
            <div className={`${styles.fadeContainer}`}>
              <h2 className={styles.frameThreeHeading}>
                {ionTrackingDevice3dSectionHoriCardsData[0].heading}
              </h2>

              <div ref={frameThreeViewportRef} className={styles.frameThreeHorizontalViewport}>
                <div ref={frameThreeTrackRef} className={styles.frameThreeHorizontalSection}>
                  <Scene3CardsSliderV15
                    cardsData={horizontalCards}
                    arrowSourceData={ionTrackingDeviceAppliedAcrossComplexData}
                  />
                </div>
              </div>
            </div>
          </section>


          {/* ---------- Scene4 : Miniaturized. Modular. Powerful. ---------- */}
          <section ref={frameFourRef} className={`${styles.scrollTextCard} ${styles.overlayFrameFour}`}>
            <div className={`${styles.fadeContainer}`}>
              <div className={`${styles.imgsContainer}`}>
                <Image
                  className={`${styles.penny}`}
                  src={ionTrackingDevice3dSectionPennyScaleData.gallery[0].url}
                  alt={ionTrackingDevice3dSectionPennyScaleData.gallery[0].alt}
                  width={225}
                  height={225}
                />

                <Image
                  className={`${styles.pennyMeasureSVG}`}
                  src={ionTrackingDevice3dSectionPennyScaleData.gallery[1].url}
                  alt={ionTrackingDevice3dSectionPennyScaleData.gallery[1].alt}
                  width={35}
                  height={306}
                />

                <Image
                  className={`${styles.pennyWithMeasure}`}
                  src={ionTrackingDevice3dSectionPennyScaleData.gallery[2].url}
                  alt={ionTrackingDevice3dSectionPennyScaleData.gallery[2].alt}
                  width={225}
                  height={225}
                />
              </div>

              <div className={styles.frameFourCopy}>
                <h3>
                  {ionTrackingDevice3dSectionPennyScaleData.heading}
                </h3>
                <p>
                  {ionTrackingDevice3dSectionPennyScaleData.body}
                </p>
              </div>
            </div>
          </section>


          {/* ---------- Scene5 : Always On. Always Aware. ---------- */}
          <section ref={frameFiveRef} className={`${styles.scrollTextCard} ${styles.overlayFrameFive}`}>
            <div className={`${styles.fadeContainer}`}>
              <div className={styles.frameFiveCopy}>
                <h3>
                  {parse(ionTrackingDevice3dSection5Data[0].heading)}
                </h3>
                <p>
                  {ionTrackingDevice3dSection5Data[0].body}
                </p>
              </div>
            </div>
          </section>


          {/* ---------- Scene6 : No Intermediaries. No Infrastructure. ---------- */}
          <section ref={frameSixRef} className={`${styles.scrollTextCard} ${styles.overlayFrameSix}`}>
            <div className={`${styles.fadeContainer}`}>
              <div className={styles.frameSixCopy}>
                <h3>
                  {parse(ionTrackingDevice3dSection5Data[1].heading)}
                </h3>
                <p>
                  {ionTrackingDevice3dSection5Data[1].body}
                </p>
              </div>
            </div>
          </section>


          {/* ---------- Scene7 : Core capabilities ---------- */}
          <section ref={frameSevenRef} className={`${styles.scrollTextCard} ${styles.overlayFrameSeven}`}>
            <div className={`${styles.fadeContainer}`}>
              <div className={styles.frameSevenCopy}>
                <h2>
                  {ionTrackingDevice3dSectionCoreCapabilitiesData[0]?.__typename === "HeadingGalleryRecord"
                    ? ionTrackingDevice3dSectionCoreCapabilitiesData[0].heading
                    : ""}
                </h2>

                <div className={styles.whiteBkgdCopyContainer}>
                  <div ref={frameSevenImageRef} className={styles.imgContainer}>
                    {ionTrackingDevice3dSectionCoreCapabilitiesData[0]?.__typename === "HeadingGalleryRecord" &&
                    ionTrackingDevice3dSectionCoreCapabilitiesData[0].gallery[0] ? (
                      <Image
                        className={`${styles.trackingDevice}`}
                        src={ionTrackingDevice3dSectionCoreCapabilitiesData[0].gallery[0].url}
                        alt={ionTrackingDevice3dSectionCoreCapabilitiesData[0].gallery[0].alt}
                        width={300}
                        height={263}
                      />
                    ) : null}
                  </div>
                  <ul>
                    <li>
                      {ionTrackingDevice3dSectionCoreCapabilitiesData[1]?.__typename === "BodyRecord"
                        ? ionTrackingDevice3dSectionCoreCapabilitiesData[1].body
                        : ""}
                    </li>
                    <li>
                      {ionTrackingDevice3dSectionCoreCapabilitiesData[2]?.__typename === "BodyRecord"
                        ? ionTrackingDevice3dSectionCoreCapabilitiesData[2].body
                        : ""}
                    </li>
                    <li>
                      {ionTrackingDevice3dSectionCoreCapabilitiesData[3]?.__typename === "BodyRecord"
                        ? ionTrackingDevice3dSectionCoreCapabilitiesData[3].body
                        : ""}
                    </li>
                    <li>
                      {ionTrackingDevice3dSectionCoreCapabilitiesData[4]?.__typename === "BodyRecord"
                        ? ionTrackingDevice3dSectionCoreCapabilitiesData[4].body
                        : ""}
                    </li>
                    <li>
                      {ionTrackingDevice3dSectionCoreCapabilitiesData[5]?.__typename === "BodyRecord"
                        ? ionTrackingDevice3dSectionCoreCapabilitiesData[5].body
                        : ""}
                    </li>
                  </ul>
                </div>

                <div className={styles.specifications}>
                  <p></p>
                  <p>
                    {
                      ionTrackingDevice3dSectionCoreCapabilitiesData[6]?.__typename === "BodyRecord"
                      ? ionTrackingDevice3dSectionCoreCapabilitiesData[6].body
                      : ""
                    }
                  </p>
                </div>
              </div>
            </div>
          </section>


          {/* ---------- Scene8 : Implementation Process ---------- */}
          <section 
            ref={frameEightRef}
            className={`${styles.scrollTextCard} ${styles.overlayFrameEight}`}
          >
            <div className={`${styles.fadeContainer}`}>
              <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
                <div className={`${styles.headingContainer}`}>
                  <h2>
                    {
                      ionTrackingDeviceImplementationProcessData[0]?.__typename === "HeadingRecord"
                      ? ionTrackingDeviceImplementationProcessData[0].heading
                      : ""
                    }
                  </h2>
                </div>

                <div className={`${styles.overlinesCopyImgsContainer}`}>
                  <div className={`${styles.overlinesContainer}`}>
                    {ionTrackingDeviceImplementationProcessData
                      .slice(1, 6)
                      .map((item, index) => (
                        <div
                          key={`implementation-overline-${index}`}
                          className={`${styles.overline}`}
                        >
                          <p>
                            {item?.__typename === "OverlineBodyGalleryRecord"
                              ? item.overline
                              : ""}
                          </p>
                          <div className={`${styles.bar}`}></div>
                        </div>
                      ))}
                  </div>

                  <div className={`${styles.copyImgsContainer}`}>
                    {ionTrackingDeviceImplementationProcessData
                      .slice(1, 6)
                      .map((item, index) => {
                        if (item?.__typename !== "OverlineBodyGalleryRecord") {
                          return null;
                        }

                        const image = item.gallery?.[0];

                        return (
                          <div
                            key={`implementation-copy-img-${index}`}
                            className={`${styles.copyImgContainer}`}
                          >
                            <div className={`${styles.imgContainer}`}>
                              {image ? (
                                <Image
                                  src={image.url}
                                  alt={image.alt}
                                  width={60}
                                  height={60}
                                />
                              ) : null}
                            </div>
                            <p>{item.body}</p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* ---------- Scene9 : From safety-critical... ---------- */}
          <section
            ref={frameNineRef}
            // className={`${styles.scene9} ${styles.overlayFrameNine}`}
            className={`${styles.scrollTextCard} ${styles.overlayFrameNine}`}
          >
            <div className={`${styles.fadeContainer}`}>
              <div ref={scene9Ref} className={`${styles.maxWidthContainer} maxWidthContainer`}>
                <div className={styles.copyContainer}>
                  <h2>{parse(ionTrackingDevicePag2Section1Data.heading)}</h2>
                </div>
              </div>
            </div>
            <div className={styles.svgArrowWrap}>
              {/* <Image
                className={styles.svgArrowBase}
                src={ionTrackingDevicePag2Section1Data.gallery[0].url}
                alt={ionTrackingDevicePag2Section1Data.gallery[0].alt}
                width={10}
                height={100}
              /> */}
              <MotionImage
                className={styles.svgArrowDraw}
                src={ionTrackingDevicePag2Section1Data.gallery[0].url}
                alt={ionTrackingDevicePag2Section1Data.gallery[0].alt}
                width={10}
                height={100}
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{
                  clipPath: [
                    "inset(0 0 100% 0)",
                    "inset(0 0 0% 0)",
                    "inset(100% 0 0% 0)",
                  ],
                }}
                transition={{
                  duration: 1.8,
                  ease: "linear",
                  repeat: Infinity,
                  repeatDelay: 0.2,
                  times: [0, 0.5, 1],
                  repeatType: "loop",
                }}
              />
            </div>
          </section>


          {/* ---------- Scene10 : Real-time visibility of people and assets ---------- */}
          <div className={styles.sceneTenSequence}>
            <div className={styles.sceneTenPaginationShell}>
              <div
                className={`${styles.sceneTenPagination} ${
                  sceneTenPaginationVisible ? styles.sceneTenPaginationVisible : styles.sceneTenPaginationHidden
                }`}
              >
                {stickyContent.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSceneTenSection(index)}
                    aria-label={`Scroll to scene 10 section ${index + 1}`}
                    className={`${styles.paginationItem} ${
                      index <= sceneTenActiveIndex
                        ? styles.pastPaginationItem
                        : styles.upcomingPaginationItem
                    }`}
                  />
                ))}
              </div>
            </div>

            {renderSceneTenSection(frameTenRef, frameTenFadeRef, styles.overlayFrameTen, scene10_1)}
            {renderSceneTenSection(frameElevenRef, frameElevenFadeRef, styles.overlayFrameEleven, scene10_2)}
            {renderSceneTenSection(frameTwelveRef, frameTwelveFadeRef, styles.overlayFrameTwelve, scene10_3)}
            {renderSceneTenSection(frameThirteenRef, frameThirteenFadeRef, styles.overlayFrameThirteen, scene10_4)}
            {renderSceneTenSection(frameFourteenRef, frameFourteenFadeRef, styles.overlayFrameFourteen, scene10_5)}
          </div>



          {/* ---------- Scene15 : Applied Across Complex... ---------- */}
          <section
            ref={frameFifteenRef}
            className={`${styles.scrollTextCard} ${styles.overlayFrameFifteen}`}
          >
            <div className={`${styles.fadeContainer}`}>
              <div className={`${styles.sceneElevenTopRow}`}>
                <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
                  <div className={`${styles.copyContainer}`}>
                    <span>
                      {appliedAcrossComplexHero?.overline ?? ""}
                    </span>
                    <h2>
                      {appliedAcrossComplexHero?.heading ?? ""}
                    </h2>
                    <p>
                      {appliedAcrossComplexHero?.body ?? ""}
                    </p>
                  </div>
                  <div className={`${styles.imgContainer}`}>
                    {appliedAcrossComplexHero?.gallery?.[0] ? (
                      <Image
                        src={appliedAcrossComplexHero.gallery[0].url}
                        alt={appliedAcrossComplexHero.gallery[0].alt}
                        // width={600}
                        // height={471}
                        fill
                      />
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={`${styles.sceneElevenBtmRow}`}>
                <div className={`${styles.maxWidthContainer} maxWidthContainer`}>

                  <Scene11CardsSliderV15
                    imgsData={ionTrackingDeviceAppliedAcrossComplexData}
                  />
                </div>
              </div>
            </div>
          </section>


          {/* ---------- Scene16 : Footer specific to TrackingDeviceOverlay ---------- */}
          <section className={`${styles.overlayFrameSixteen}`}>
            <FooterViewV15 footerData={footerData} />
          </section>


        {/* <div ref={overlaySpaceRef} className={styles.overlayScrollSpace} aria-hidden>
          {Array.from({ length: Math.max(stepCount, OVERLAY_STEPS) }).map((_, index) => (
            <div
              key={`overlay-step-${index}`}
              className={styles.overlayScrollStep}
            />
          ))}
        </div> */}

    </div>
  );
});

TrackingDeviceOverlay.displayName = "TrackingDeviceOverlay";

export default TrackingDeviceOverlay;
